import React, { useContext, useEffect, useState } from 'react';

import { Place } from '../../app/models/Place';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';

import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';

import { toast } from 'react-toastify';

import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useCurrencyService from '../../app/services/hooks/useCurrencyService';
import { Currency, CurrencyFilters } from '../../app/models/Currency';
import CurrencyDataTable from './components/tables/CurrencyDataTable';
import CurrencyCreate from './components/CurrencyCreate';
import CurrencyEdit from './components/CurrencyEdit';
import { useHistory } from 'react-router-dom';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Monedas',
        url: '/currencies',
        isActive: true
    }
];

const Currencies = () => {
    const history = useHistory();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetCurrencies, getCurrencies, deleteCurrency, activeCurrency } =
        useCurrencyService();
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const { navigationPage } = useNavigationPage();

    const [currencyIdEdit, setCurrencyIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [currencyFilter, setCurrencyFilter] = useState<CurrencyFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [currencyFilter]);

    const getAllCurrencies = () => {
        getCurrencies(currencyFilter, {
            onSuccess: (response: ServiceResponse) => {
                setCurrencies(response.data.currencies);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });

                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (currencyId: number) => {
        setShowingEdit(true);
        setCurrencyIdEdit(currencyId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setCurrencyIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllCurrencies();
    };

    const destroy = (currencyId: number) => {
        const _text = 'Está a punto de eliminar la moneda #' + currencyId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando moneda...');
                        deleteCurrency(currencyId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La moneda no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const active = (currencyId: number, active: boolean) => {
        activeCurrency(currencyId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Monedas" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nueva moneda" />}
                />
                <CurrencyDataTable
                    currencies={currencies}
                    edit={showEdit}
                    destroy={destroy}
                    active={active}
                    loading={fetchingGetCurrencies}
                    filter={currencyFilter}
                    setFilter={setCurrencyFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear moneda"
                    backdrop={true}
                    showFooter={false}
                >
                    <CurrencyCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar moneda"
                    backdrop={true}
                    showFooter={false}
                >
                    <CurrencyEdit
                        currencyId={currencyIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Currencies;
